import { useState } from "react";
import { faUserSlash } from "@fortawesome/free-solid-svg-icons";
import { ConfirmDialog } from "@/components/common/Dialog";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";
import { showSuccessToast } from "@/hooks/use-toast";
import { revokeAiUserAccess } from "../api";
import type { AiUserAccessRow } from "../types";

export type RevokeAiUserAccessDialogProps = {
  user: AiUserAccessRow;
  callBack: () => Promise<void>;
};

export const RevokeAiUserAccessDialog = ({ user, callBack }: RevokeAiUserAccessDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setError(null);
  };

  const handleConfirmRevoke = async () => {
    setLoading(true);
    setError(null);
    try {
      await revokeAiUserAccess(user.userId);
      showSuccessToast("Access revoked successfully.");
      setDialogOpen(false);
      await callBack();
    } catch (err) {
      setError("Failed to revoke access.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay isVisible={loading} message="Revoking access..." />
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        triggerIcon={faUserSlash}
        triggerText=""
        triggerVariant="icon"
        triggerTitle="Revoke access"
        title="Revoke AI access?"
        description={
          <>
            This will revoke AI access for <strong>{user.displayName || user.username}</strong>.
            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          </>
        }
        confirmText="Revoke"
        cancelText="Cancel"
        loading={loading}
        onConfirm={handleConfirmRevoke}
        disableConfirm={loading}
      />
    </>
  );
};
